/**
 * Which Management dashboard views a user may open — the ONE copy of the
 * rule. ManagementDashboard uses it for its nav and landing view; Header uses
 * it to decide whether the Management button shows at all. Each view needs
 * the capability its API requires (the backend enforces each with a 403).
 * The Support Inbox also needs the server's chat flag (`features.chat`): with
 * chat off its API is a 404, so a supporter with no other view gets no
 * dashboard and no Management button.
 */
import { CAP } from "@/api/capabilities";
import { useAppSelector } from "@/state/hooks";

/** Nav order: the dashboard opens on the first of these the user may see. */
export const VIEW_ORDER = [
  "users",
  "sessions",
  "roles",
  "traders",
  "usage",
  "terminal",
  "support",
] as const;

export type View = (typeof VIEW_ORDER)[number];

/** The capability each view requires. */
const VIEW_CAP: Record<View, string> = {
  // The roster, sessions and role matrix all come from dashboard.view routes.
  users: CAP.dashboardView,
  sessions: CAP.dashboardView,
  roles: CAP.dashboardView,
  // Approving a trader grants the ability to place real orders, so it has
  // its own capability rather than riding on users.manage.
  traders: CAP.tradersManage,
  usage: CAP.usageView,
  terminal: CAP.terminalUse,
  support: CAP.chatSupport,
};

export interface ManagementViews {
  allowed: Record<View, boolean>;
  /** The allowed views, in nav order. */
  views: View[];
}

/** Views that exist only while a server feature flag is on. */
const VIEW_FEATURE: Partial<Record<View, keyof Features>> = { support: "chat" };

export interface Features {
  chat: boolean;
}

export function managementViews(
  capabilities: readonly string[],
  features: Features,
): ManagementViews {
  const allowed = Object.fromEntries(
    VIEW_ORDER.map((v) => {
      const feature = VIEW_FEATURE[v];
      return [v, capabilities.includes(VIEW_CAP[v]) && (!feature || features[feature])];
    }),
  ) as Record<View, boolean>;
  return { allowed, views: VIEW_ORDER.filter((v) => allowed[v]) };
}

/** Whether the user may open the dashboard at all (≥1 allowed view). */
export function hasAnyManagementView(
  capabilities: readonly string[],
  features: Features,
): boolean {
  return managementViews(capabilities, features).views.length > 0;
}

export function useManagementViews(): ManagementViews {
  const capabilities = useAppSelector((s) => s.me.capabilities);
  const chat = useAppSelector((s) => s.me.chatEnabled);
  return managementViews(capabilities, { chat });
}
